import {
  CandidateRow,
  ElementOriginMapping,
  MaterializeMissing,
  MaterializeResult,
  ProjectElementImport,
  ResourceCodeMapping,
  ResourceCodeMappingEntry,
} from '@shared/models/element-import.model';
import {
  ElementKind,
  CsvImportTrigger,
  ElementOrigin,
  IElement,
  ProductElementOverrideTarget,
} from '@shared/models/project.model';
import { getObjectHash, sortObject } from './object.helpers';
import { assertUnreachable } from './function.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  ElementQuantityExpressionName,
  elementQuantityUnitRecord,
  iconQuantityNames,
} from '@shared/models/element-quantities.model';
import { IProduct } from '@shared/models/product.model';
import { Recipe } from '@shared/models/recipe.model';
import { ConversionFactorUnit } from '@shared/models/unit.model';
import { unitAndQuantityHarmonizer } from './unit.helpers';
import {
  canConvertConversionFactors,
  convertCount,
  getCorrespondingConversionFactorUnit,
} from './conversion.helpers';
import { isElementQuantityExpressionName } from './element-quantity.helpers';
import { resolveAreaOrientationFromRow } from './import-area-orientation.helpers';
import { roundToDecimals } from './math.helpers';
import {
  applyCategoryChildElements,
  applyElementCategoryPropertySelections,
  createElementWithCategory,
  getElementCategory,
  getElementCategoryAndSelectionsByProductId,
} from './element-category.helpers';
import { setProductOverride } from './product-element.helpers';
import { applyRecipe } from './recipe.helpers';
import { isProductElement } from './recursive-element.helpers';
import {
  WasteScale,
  parseWasteCell,
  wasteValueToPercent,
} from './waste-scale.helpers';
import {
  getSelectedQuantityName,
  setElementQuantityProperties,
} from './element-quantity-core.helpers';
import { createExpression } from './expression-factory.helpers';
import { setPropertyCountByNameOrId } from './element-property.helpers';
import { getSbefCode } from './sbef.helpers';
import {
  resolveActiveIfcMeasure,
  type IfcMeasure,
} from './ifc-quantity.helpers';
import {
  categoryDefinesQuantity,
  resolveImportQuantityByPriority,
} from './import-quantity-priority.helpers';
import {
  listImportQuantitySources,
  resolveImportQuantity,
} from './import-quantity-resolution.helpers';
import {
  candidateFieldValue,
  candidateQuantityFrom,
  candidateUnitFrom,
  harmonizeCandidateUnitString,
} from './candidate-row-cells.helpers';

const MISSING_LABELS: Record<MaterializeMissing, string> = {
  product: 'product',
  recipe: 'recipe',
  unitFrom: 'CSV unit',
  unitTo: 'product unit',
  quantityFactor: 'quantity factor',
  quantity: 'quantity',
};

const missingLabel = (m: MaterializeMissing): string => MISSING_LABELS[m];

const fieldValue = candidateFieldValue;

export { candidateQuantityFrom, candidateUnitFrom };

/**
 * The human-readable name for a candidate row -- from the resolved name
 * column. Falls back to {@link CandidateRow.name} when no name column exists.
 */
export const candidateName = (row: CandidateRow): string | undefined =>
  fieldValue(row, 'name') ?? row.name ?? undefined;

/** SBEF code string from the row's resolved SBEF column. */
export const candidateSbef = (row: CandidateRow): string | undefined =>
  fieldValue(row, 'sbef');

/**
 * The raw waste / spill number for a candidate row, when a waste column
 * carries one. This is the kalkylfil "Spill" cell as-is -- a spillfaktor,
 * not a percentage (see {@link candidateWastePercent}).
 */
export const candidateSpill = (row: CandidateRow): number | undefined => {
  const v = fieldValue(row, 'waste');
  return v === undefined ? undefined : parseWasteCell(v);
};

/**
 * The waste / spill as the 0--100 percentage the
 * {@link ProductElementOverrideTarget.WASTE_PERCENT} override expects.
 *
 * The raw cell value is interpreted with the column's detected
 * {@link WasteScale} (frozen on the import metadata at parse time). Returns
 * `undefined` -- the product's native waste factor stands -- when there is no
 * scale is absent, or the row has no waste value.
 */
export const candidateWastePercent = (
  row: CandidateRow,
  scale: WasteScale | undefined,
): number | undefined => {
  if (!scale) return undefined;
  const value = candidateSpill(row);
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const percent = wasteValueToPercent(value);
  // Reject values that do not map to a sensible 0–100% override.
  if (percent < 0 || percent > 100) return undefined;
  return roundToDecimals(percent, 6);
};

const harmonizeUnit = harmonizeCandidateUnitString;

/** The product-covered conversion for one (unit, quantity) pair, if any. */
const productConversion = (
  product: IProduct,
  unit: string,
  quantity: number | undefined,
): CandidateConversion | undefined => {
  const harmonized = harmonizeUnit(unit);
  if (!harmonized) return undefined;
  const factor = product.conversion_factors[harmonized.unit];
  if (!factor) return undefined;
  return {
    unit: harmonized.unit,
    rowUnit: unit,
    factor,
    quantity,
    swapped: false,
  };
};

export type CandidateConversion = {
  /** Harmonized CSV unit the factor is keyed by. */
  unit: ConversionFactorUnit;
  /**
   * Row-side spelling of the measure the conversion ran on (`m3`, not `m³`).
   * Mapping entries are keyed by what {@link candidateUnitFrom} returns, so a
   * swapped measure must file its rate under the column's own spelling.
   */
  rowUnit: string;
  /** The product's conversion factor for {@link unit}. */
  factor: number;
  /** The row quantity this conversion applies to (differs from the row's own when swapped). */
  quantity: number | undefined;
  /** True when the row's picked measure was swapped for another IFC measure. */
  swapped: boolean;
};

/**
 * The conversion the chosen product already knows for a candidate row's CSV
 * unit. When the product's own `conversion_factors` cover the (harmonized)
 * unit, no manual unit/factor mapping is needed -- the product is the source
 * of truth. Returns `undefined` when a manual mapping is still required.
 *
 * An IFC row whose picked measure the product cannot convert falls back to the
 * *other* measures the same row carries (in the IFC type's priority order) and
 * imports through the first one the product does cover -- the file already
 * holds a convertible number, so no manual factor should be asked for. The
 * returned `quantity` is the swapped measure's value, not the row's own.
 *
 * Pass `allowIfcSwap: false` when the mapping already carries a manual
 * `quantityFactor`: that rate was calibrated against the row's own quantity and
 * unit, so a swap would silently replace what the user typed.
 */
export const candidateConversion = (
  row: CandidateRow,
  product: IProduct | undefined,
  options: { allowIfcSwap?: boolean } = {},
): CandidateConversion | undefined => {
  if (!product) return undefined;
  const unitFrom = candidateUnitFrom(row);

  for (const source of listImportQuantitySources(row)) {
    const isOwn = source.rowUnit === unitFrom;
    if (!isOwn && options.allowIfcSwap === false) continue;
    const conversion = productConversion(
      product,
      source.rowUnit,
      source.quantity,
    );
    if (conversion) return { ...conversion, swapped: !isOwn };
  }
  return undefined;
};

/**
 * True when CSV unit and Amount unit are the same physical unit, so a factor
 * of 1 is the identity — not a conversion to persist, show, or write as
 * `"qty * 1"`.
 */
export const isIdentityConversionFactor = (
  csvUnit: string | undefined,
  amountUnit: string | undefined,
  factor: number | undefined,
): boolean => {
  if (factor !== 1 || !csvUnit || !amountUnit) return false;
  let csv: ConversionFactorUnit | undefined;
  try {
    csv = unitAndQuantityHarmonizer(1, csvUnit)?.unit;
  } catch {
    return false;
  }
  const amount = getCorrespondingConversionFactorUnit(amountUnit, false);
  return csv !== undefined && amount !== undefined && csv === amount;
};

/**
 * Count expression for an imported quantity. Identity factor → bare number.
 */
export const importQuantityExpression = (
  quantityFrom: number,
  factor: number,
): string =>
  factor === 1 ? String(quantityFrom) : `${quantityFrom} * ${factor}`;

/**
 * Stable hash of the materialization mapping. The same object is hashed whether
 * held inline on {@link ElementOrigin} or written to the resource-code table, so
 * an inline-vs-stored comparison later is plain string equality.
 */
export const hashImportMapping = (mapping: ElementOriginMapping): string =>
  getObjectHash(sortObject(mapping));

/** The inline mapping snapshot a row materializes through, mirroring the DB row shape. */
const buildOriginMapping = (
  product: IProduct,
  unitFrom: string,
  entry: ResourceCodeMappingEntry,
): ElementOriginMapping => ({
  referenceId: product.id,
  referenceType: 'product',
  mappings: { [unitFrom]: entry },
});

/**
 * Build the `count` expression for an imported product element. The original
 * CSV quantity and the conversion factor are kept as visible terms in the
 * expression string (e.g. `"10 / 2"`); bare numbers only -- mathjs units
 * cannot be used because `solve()` rejects non-number results.
 */
const buildCountExpression = (
  quantityFrom: number,
  factor: number,
  operator: '*' | '/',
) => {
  const resolved =
    operator === '*' ? quantityFrom * factor : quantityFrom / factor;
  const expression =
    factor === 1
      ? String(quantityFrom)
      : `${quantityFrom} ${operator} ${factor}`;
  return createExpression(expression, resolved);
};

/**
 * Validate a candidate row against its resource-code mapping entry and, when
 * complete, build the project element it materializes into (DEV-3298).
 *
 * The element is created through the category system: the mapped product
 * resolves to an element category + property selections
 * ({@link getElementCategoryAndSelectionsByProductId}); the category owns the
 * product child (generated from the selection during enrichment). The
 * converted Mängd is written to the element's selected quantity as a maths
 * expression, and `sbef_code` is set best-effort from the SBEF column.
 *
 * When `product`'s conversion factors already cover the candidate's CSV unit
 * (see {@link candidateConversion}), the manual `unitTo` / `quantityFactor`
 * are not required.
 */
export const materializeCandidate = (
  row: CandidateRow,
  entry: ResourceCodeMappingEntry,
  product: IProduct | undefined,
  wasteScale?: WasteScale,
  /**
   * Total quantity the user typed in-row (DEV-3369). When present it is written
   * as the count's `value` override on top of the conversion expression, so the
   * element keeps both the formula (`"<qty> * <factor>"`) and the user's number.
   */
  valueOverride?: number,
  /**
   * Quantity row the element should be measured in (e.g. `area_top`). When set,
   * the converted Mängd is written to that quantity (not the category default)
   * and the element is created with it selected. When a calibrated
   * {@link ResourceCodeMappingEntry.quantityFactor} is also present, that
   * manual factor wins; otherwise product factors are re-expressed into this
   * quantity's unit (so m² CSV → m² Amount stays 1:1, not kg/m²).
   */
  selectedQuantity?: ElementQuantityExpressionName,
  options?: {
    /** The row's Quantity from file pick — tried first, never a hard lock. */
    pinnedMeasure?: IfcMeasure;
  },
): MaterializeResult => {
  // The explicit override (live UI pick) wins; otherwise the persisted entry's
  // quantityName is the source of truth (survives DB / reload, disambiguates the
  // unit, e.g. `m²` -> `area_side`).
  const effectiveSelectedQuantity = selectedQuantity ?? entry.quantityName;

  // Which of the row's numbers this product imports, decided once and shared
  // with the strip, the measure picker and the rate label.
  const resolution = product
    ? resolveImportQuantity({
        row,
        target: { kind: 'product', product },
        pinnedMeasure: options?.pinnedMeasure,
        explicitQuantity: effectiveSelectedQuantity,
        manualFactor: entry.quantityFactor,
      })
    : undefined;
  const resolved =
    resolution?.status === 'unresolvable' ? undefined : resolution;

  // The product's own auto-conversion, absent when the user's rate applies.
  const conversion: CandidateConversion | undefined =
    resolved?.status === 'resolved' &&
    resolved.via === 'auto' &&
    resolved.unit !== undefined
      ? {
          unit: resolved.unit,
          rowUnit: resolved.rowUnit,
          factor: resolved.factor,
          quantity: resolved.quantity,
          swapped: resolved.measure !== undefined,
        }
      : undefined;

  // A manual factor is calibrated against the row's own quantity; only the
  // auto-conversion path may run on a swapped IFC measure's quantity.
  const useManualFactor =
    effectiveSelectedQuantity !== undefined &&
    entry.quantityFactor !== undefined;

  // The unit the mapping is keyed by: whichever measure the import actually ran
  // on. A swapped IFC column owns the rate -- filing it under the row's
  // parse-time unit would describe a factor against a quantity it never used.
  const unitKey = resolved?.rowUnit ?? candidateUnitFrom(row);
  const quantityFrom = resolved?.quantity ?? candidateQuantityFrom(row);

  const missing: MaterializeMissing[] = [];
  if (!unitKey) missing.push('unitFrom');
  if (!product) missing.push('product');
  if (!conversion) {
    if (!entry.unitTo) missing.push('unitTo');
    if (entry.quantityFactor === undefined) missing.push('quantityFactor');
  }
  if (quantityFrom === undefined) missing.push('quantity');

  if (
    missing.length > 0 ||
    !product ||
    !unitKey ||
    quantityFrom === undefined
  ) {
    return {
      ok: false,
      missing,
      reason: `Missing ${missing.map(missingLabel).join(', ')}.`,
    };
  }

  // Resolve the element category + property selections for the mapped product
  // (a material category, or OtherProduct as fallback). Needed before the count
  // expression so we know which Amount unit the Mängd lands in.
  const { category_id, selections } =
    getElementCategoryAndSelectionsByProductId(product.id) ?? {
      category_id: ElementCategoryID.OtherProduct,
      selections: {},
    };

  const targetQuantityName =
    resolveProductQuantityName({
      product,
      row,
      explicitQuantity: effectiveSelectedQuantity,
    }) ?? getElementCategory(category_id)?.defaultSelectedQuantity;
  const targetUnit =
    targetQuantityName && isElementQuantityExpressionName(targetQuantityName)
      ? getCorrespondingConversionFactorUnit(
          elementQuantityUnitRecord[targetQuantityName],
          false,
        )
      : undefined;

  let csvHarmonized: ReturnType<typeof unitAndQuantityHarmonizer> | undefined;
  try {
    csvHarmonized = unitAndQuantityHarmonizer(1, unitKey) ?? undefined;
  } catch {
    csvHarmonized = undefined;
  }

  // Factor that lands `qtyFrom` in the Amount quantity unit (m² per m² → 1),
  // not the product's native unit (kg). Using `/ conversion.factor` alone would
  // write a kg-magnitude into an m² Amount row for doors/windows.
  const amountUnitFactor = (() => {
    if (!csvHarmonized || !targetUnit) return undefined;
    if (
      !canConvertConversionFactors(
        product.conversion_factors,
        csvHarmonized.unit,
      ) ||
      !canConvertConversionFactors(product.conversion_factors, targetUnit)
    ) {
      return undefined;
    }
    const converted = convertCount(
      quantityFrom,
      product.conversion_factors,
      csvHarmonized.unit,
      targetUnit,
    );
    if (!Number.isFinite(converted) || converted <= 0) return undefined;
    return converted / quantityFrom;
  })();

  // Count expression:
  // 1. Manual factor calibrated for a picked/persisted quantity (*).
  // 2. Product factors re-expressed into the Amount unit (*).
  // 3. Legacy product-unit auto-conversion (/ factor).
  const resolvedEntry: ResourceCodeMappingEntry =
    !useManualFactor &&
    entry.quantityFactor === undefined &&
    amountUnitFactor !== undefined &&
    targetUnit &&
    isElementQuantityExpressionName(targetQuantityName)
      ? {
          ...entry,
          unitTo: targetUnit,
          quantityName: targetQuantityName,
          // Native 1:1 (m² CSV → m² Amount): keep quantityName, skip the * 1 factor.
          ...(isIdentityConversionFactor(
            csvHarmonized?.unit,
            targetUnit,
            amountUnitFactor,
          )
            ? {}
            : { quantityFactor: amountUnitFactor }),
        }
      : entry;

  const baseCount = (() => {
    if (useManualFactor) {
      return buildCountExpression(
        quantityFrom,
        entry.quantityFactor as number,
        '*',
      );
    }
    if (amountUnitFactor !== undefined) {
      return buildCountExpression(quantityFrom, amountUnitFactor, '*');
    }
    if (conversion) {
      return buildCountExpression(quantityFrom, conversion.factor, '/');
    }
    return buildCountExpression(
      quantityFrom,
      entry.quantityFactor as number,
      '*',
    );
  })();
  // A typed total quantity becomes a number override on the count, keeping the
  // conversion expression as the formula (mirrors the Elements-tab override).
  const count =
    valueOverride !== undefined && Number.isFinite(valueOverride)
      ? { ...baseCount, value: valueOverride }
      : baseCount;

  const originMapping = buildOriginMapping(product, unitKey, resolvedEntry);
  const origin: ElementOrigin = {
    kind: 'csvImport',
    importId: row.importId,
    candidateRowId: row.id,
    mappingHash: hashImportMapping(originMapping),
    source: 'inline',
    mapping: originMapping,
  };

  let element = createElementWithCategory({
    kind: ElementKind.Element,
    category_id,
    name: candidateName(row) ?? row.name ?? '',
    origin,
  });
  element = applyElementCategoryPropertySelections(element, selections);

  // A unit-pick steers which quantity row the Mängd is written to; select it so
  // the converted value lands there instead of the category default.
  const quantityName =
    effectiveSelectedQuantity ??
    (isElementQuantityExpressionName(targetQuantityName)
      ? targetQuantityName
      : undefined) ??
    getElementCategory(category_id)?.defaultSelectedQuantity;
  if (quantityName && isElementQuantityExpressionName(quantityName)) {
    element = { ...element, selectedQuantity: quantityName };
  }

  // The generated product child's count references the element's selected
  // quantity, so the converted Mängd is written there.
  if (quantityName && isElementQuantityExpressionName(quantityName)) {
    element = setElementQuantityProperties(element, [
      { name: quantityName, count },
    ]);
  }

  // Best-effort sbef_code from CSV SBEF/kod/byggdel columns; left empty when it does not
  // resolve to a valid SBEF code.
  const sbefCode = getSbefCode(candidateSbef(row));
  if (sbefCode) {
    element = setPropertyCountByNameOrId(
      element,
      ElementPropertyName.SbefCode,
      sbefCode,
    );
  }

  // Spill: the CSV waste value replaces the product's native waste factor.
  // The product child is generated here (the same pure step enrichment runs)
  // so the WASTE_PERCENT override has a home; enrichment later reuses the
  // same product slot and keeps the override.
  const wastePercent = candidateWastePercent(row, wasteScale);
  if (wastePercent !== undefined) {
    element = applyCategoryChildElements(element);
    element = {
      ...element,
      elements: element.elements.map((child) =>
        isProductElement(child)
          ? setProductOverride(
              child,
              ProductElementOverrideTarget.WASTE_PERCENT,
              {
                applicationScope: 'all',
                override: { type: 'replace', value: wastePercent },
              },
            )
          : child,
      ),
    };
  }

  return { ok: true, element };
};

/**
 * Element quantities a harmonized CSV unit maps to *unambiguously* (DEV-3387).
 * `kg` is only ever Mass, `m³` only ever Volume — so a recipe row in those units
 * needs no disambiguation. `m²` (side/top/section) and `m` (length/width/height)
 * are deliberately absent: those require the user to pick which quantity.
 */
const UNAMBIGUOUS_UNIT_QUANTITY: Partial<
  Record<ConversionFactorUnit, ElementQuantityExpressionName>
> = {
  kg: 'mass',
  'm³': 'volume',
};

/** When several quantities share a unit, pick this one (m² → side area, not section). */
const PREFERRED_CSV_UNIT_QUANTITY: Partial<
  Record<ConversionFactorUnit, ElementQuantityExpressionName>
> = {
  ...UNAMBIGUOUS_UNIT_QUANTITY,
  'm²': 'area_side',
  m: 'length',
};

/**
 * Harmonized CSV unit (`m2` → `m²`), or `undefined` when the cell is empty
 * or not a physical unit.
 */
const harmonizeCandidateUnit = (
  row: CandidateRow,
): ConversionFactorUnit | undefined => {
  const unitFrom = candidateUnitFrom(row);
  if (!unitFrom) return undefined;
  try {
    return unitAndQuantityHarmonizer(1, unitFrom)?.unit;
  } catch {
    return undefined;
  }
};

/**
 * Icon quantities whose unit is an actual key in the product's conversion
 * factors. Shared with the import unit picker so Amount and the menu agree.
 */
export const getProductConvertibleQuantityNames = (
  product: IProduct,
): ElementQuantityExpressionName[] =>
  iconQuantityNames.filter(isElementQuantityExpressionName).filter((name) => {
    const unit = elementQuantityUnitRecord[name];
    const cf = unit
      ? getCorrespondingConversionFactorUnit(unit, false)
      : undefined;
    return cf
      ? canConvertConversionFactors(product.conversion_factors, cf)
      : false;
  });

/**
 * Category default Amount quantity for a product (Windows & Doors →
 * `area_side`, Insulation / OtherProduct → `mass`).
 */
const getProductCategoryDefaultQuantityName = (
  product: IProduct,
): ElementQuantityExpressionName | undefined => {
  const { category_id, selections } =
    getElementCategoryAndSelectionsByProductId(product.id) ?? {};
  if (!category_id) return undefined;

  let element = createElementWithCategory({
    kind: ElementKind.Element,
    category_id,
  });
  if (selections) {
    element = applyElementCategoryPropertySelections(element, selections);
  }
  const name =
    getSelectedQuantityName(element) ??
    getElementCategory(category_id)?.defaultSelectedQuantity;
  return isElementQuantityExpressionName(name) ? name : undefined;
};

/**
 * Which element quantity a product row's Amount fills:
 *   1. an explicit pick / persisted {@link ResourceCodeMappingEntry.quantityName},
 *   2. a convertible quantity whose unit matches the CSV unit (m² CSV + a
 *      kg/m² product → area, not Insulation/OtherProduct's mass default) —
 *      the category default wins when it is already in that set,
 *   3. the category default (Insulation → mass).
 *
 * Mass stays in the unit picker; this only chooses the default.
 */
export const resolveProductQuantityName = (params: {
  product: IProduct | undefined;
  row?: CandidateRow;
  explicitQuantity?: ElementQuantityExpressionName;
}): ElementQuantityExpressionName | undefined => {
  const { product, row, explicitQuantity } = params;
  if (explicitQuantity) return explicitQuantity;
  if (!product) return undefined;

  const categoryDefault = getProductCategoryDefaultQuantityName(product);
  if (!row) return categoryDefault;

  const csvUnit = harmonizeCandidateUnit(row);
  if (!csvUnit) return categoryDefault;

  const { category_id } = getElementCategoryAndSelectionsByProductId(
    product.id,
  ) ?? {
    category_id: ElementCategoryID.OtherProduct,
  };
  // Quantities this product can actually be measured in, within its category.
  const convertible = getProductConvertibleQuantityNames(product).filter(
    (name) => categoryDefinesQuantity(category_id, name),
  );
  const matching = convertible.filter((name) => {
    const displayUnit = elementQuantityUnitRecord[name];
    const cf = displayUnit
      ? getCorrespondingConversionFactorUnit(displayUnit, false)
      : undefined;
    return cf === csvUnit;
  });

  // The row's own words: a floor row wants `area_top`, a wall or door row
  // `area_side`. Used wherever the category cannot settle the question.
  const orientation = resolveAreaOrientationFromRow(row);

  if (matching.length === 0) {
    // The CSV unit names no quantity at all (`pcs`), so nothing can be derived
    // from it and `categoryDefault` is a blanket guess -- `mass` on
    // OtherProduct, even for a row of doors. Prefer what the row says, as long
    // as the product can be measured that way.
    if (orientation && convertible.includes(orientation)) return orientation;
    return categoryDefault;
  }
  if (categoryDefault && matching.includes(categoryDefault)) {
    return categoryDefault;
  }
  // The category could not settle it (floor tiles are Ceramics, whose default
  // is `mass`), so the row text decides before we fall back to guessing.
  if (orientation && matching.includes(orientation)) return orientation;

  const preferred = PREFERRED_CSV_UNIT_QUANTITY[csvUnit];
  if (preferred && matching.includes(preferred)) return preferred;
  return matching[0];
};

/**
 * Auto-resolve the element quantity + factor for a recipe row from its CSV unit
 * (DEV-3387). A recipe has no conversion factors, but the chosen element quantity
 * has a physical unit, so a recognizable CSV unit can map straight onto it:
 *
 * - `kg`/`m³` (and anything that harmonizes to them, e.g. `ton`, `l`) self-resolve
 *   to Mass / Volume — no quantity pick needed.
 * - `m²` / `m` are ambiguous; they resolve only once {@link selectedQuantity} names
 *   a matching quantity (its unit must equal the harmonized CSV unit).
 * - Non-physical units (`frp`, `st`/`pcs`, …) return `undefined` — a manual factor
 *   is required.
 *
 * `factor` is the value of one CSV unit in the target unit (`ton` → `1000` kg), so
 * `qtyFrom * factor` lands the Mängd in the element quantity's unit.
 */
export const recipeAutoQuantityFromUnit = (
  row: CandidateRow,
  selectedQuantity: ElementQuantityExpressionName | undefined,
):
  | { quantityName: ElementQuantityExpressionName; factor: number }
  | undefined => {
  const unitFrom = candidateUnitFrom(row);
  if (!unitFrom) return undefined;

  let harmonized: ReturnType<typeof unitAndQuantityHarmonizer>;
  try {
    harmonized = unitAndQuantityHarmonizer(1, unitFrom);
  } catch {
    return undefined;
  }
  if (!harmonized) return undefined;
  const factor = harmonized.quantity;

  // A picked quantity resolves only when its unit matches the CSV unit's dimension.
  if (selectedQuantity) {
    return elementQuantityUnitRecord[selectedQuantity] === harmonized.unit
      ? { quantityName: selectedQuantity, factor }
      : undefined;
  }

  // No pick yet: only unambiguous units self-resolve their quantity.
  const quantityName = UNAMBIGUOUS_UNIT_QUANTITY[harmonized.unit];
  return quantityName ? { quantityName, factor } : undefined;
};

/**
 * Harmonized units a candidate row can supply a Mängd in: its own CSV unit
 * first, then whatever its IFC measure columns carry.
 */
export const candidateAvailableUnits = (
  row: CandidateRow,
): Set<ConversionFactorUnit> => {
  const units = new Set<ConversionFactorUnit>();
  for (const source of listImportQuantitySources(row)) {
    const harmonized = harmonizeUnit(source.rowUnit);
    if (harmonized) units.add(harmonized.unit);
  }
  return units;
};

/**
 * Validate a candidate row against a recipe-typed mapping and, when complete,
 * build the recipe-structured project element it materializes into (DEV-3387).
 *
 * Unlike the product branch, a recipe carries no `conversion_factors`, so there
 * is never an auto-conversion — the manual `quantityFactor` is always required
 * and the converted Mängd is written to the element's selected quantity (the
 * recipe's child expressions scale against it during enrichment). The element's
 * category is the recipe's single `category_id`; {@link applyRecipe} then grafts
 * the recipe's child structure and properties on top.
 */
/**
 * Which element quantity a recipe row's Mängd fills, in priority order:
 *   1. an explicit pick / persisted choice,
 *   2. an unambiguous CSV unit (kg→Mass, m³→Volume) — but only when the
 *      category defines that quantity; a wall/slab omits mass on purpose, so a
 *      kg row falls through to the category default and waits for a typed
 *      Mängd instead (DEV-3501),
 *   3. the category's natural dimension (wall/slab→area, column/beam→length …),
 *      so a recipe always starts on a sensible quantity.
 *
 * Shared with the conversion-rate field so what the user is shown before a row
 * materializes is exactly what materializing it would choose (DEV-3456) — the
 * two resolving this independently is how they drift.
 */
export const resolveRecipeQuantityName = (params: {
  row: CandidateRow;
  categoryId: ElementCategoryID | undefined;
  explicitQuantity: ElementQuantityExpressionName | undefined;
}): ElementQuantityExpressionName | undefined => {
  const { row, categoryId, explicitQuantity } = params;
  if (explicitQuantity) return explicitQuantity;

  // The category's own quantity order, restricted to units this row can fill
  // (its own unit, or one of its IFC measure columns). Picking the natural
  // quantity the row can actually supply beats defaulting to one it cannot and
  // stalling on a manual factor -- a column row in `m` mapped to a slab recipe
  // has no area, but it does have a volume.
  const byPriority = resolveImportQuantityByPriority(
    categoryId,
    candidateAvailableUnits(row),
  );
  if (byPriority) return byPriority;

  const autoResolved = recipeAutoQuantityFromUnit(row, undefined);
  if (
    autoResolved &&
    categoryId !== undefined &&
    categoryDefinesQuantity(categoryId, autoResolved.quantityName)
  ) {
    return autoResolved.quantityName;
  }

  return categoryId !== undefined
    ? getElementCategory(categoryId)?.defaultSelectedQuantity
    : undefined;
};

export const materializeRecipeCandidate = (
  row: CandidateRow,
  entry: ResourceCodeMappingEntry,
  recipe: Recipe | undefined,
  valueOverride?: number,
  selectedQuantity?: ElementQuantityExpressionName,
  options?: {
    /** The row's Quantity from file pick — tried first, never a hard lock. */
    pinnedMeasure?: IfcMeasure;
  },
): MaterializeResult => {
  const resolution = recipe
    ? resolveImportQuantity({
        row,
        target: { kind: 'recipe', recipe },
        pinnedMeasure: options?.pinnedMeasure,
        explicitQuantity: selectedQuantity ?? entry.quantityName,
        manualFactor: entry.quantityFactor,
      })
    : undefined;
  const resolved = resolution?.status === 'resolved' ? resolution : undefined;

  // A row that cannot resolve still shows a quantity while it waits for a typed
  // Mängd -- the category's natural one.
  const effectiveSelectedQuantity =
    resolved?.quantityName ??
    resolveRecipeQuantityName({
      row,
      categoryId: recipe?.category_id,
      explicitQuantity: selectedQuantity ?? entry.quantityName,
    });

  const quantityFactor = resolved?.factor;
  const unitFrom =
    resolution && resolution.status !== 'unresolvable'
      ? resolution.rowUnit
      : candidateUnitFrom(row);
  const quantityFrom =
    resolution && resolution.status !== 'unresolvable'
      ? resolution.quantity
      : candidateQuantityFrom(row);
  // As in the product branch: the key names the measure the factor was resolved
  // against, so a swapped column owns the entry.
  const unitKey = unitFrom;

  const missing: MaterializeMissing[] = [];
  if (!unitKey) missing.push('unitFrom');
  if (!recipe) missing.push('recipe');
  if (quantityFactor === undefined) missing.push('quantityFactor');
  if (quantityFrom === undefined) missing.push('quantity');

  if (
    missing.length > 0 ||
    !recipe ||
    !unitKey ||
    quantityFrom === undefined ||
    quantityFactor === undefined
  ) {
    return {
      ok: false,
      missing,
      reason: `Missing ${missing.map(missingLabel).join(', ')}.`,
    };
  }

  const baseCount = buildCountExpression(quantityFrom, quantityFactor, '*');
  const count =
    valueOverride !== undefined && Number.isFinite(valueOverride)
      ? { ...baseCount, value: valueOverride }
      : baseCount;

  // Snapshot the resolved factor + quantity so the origin mapping is complete
  // even when both came from auto-resolution (kg→Mass etc.), keeping the baseline
  // stable across reloads.
  const resolvedEntry: ResourceCodeMappingEntry = {
    ...entry,
    quantityFactor,
    quantityName: effectiveSelectedQuantity,
    unitTo:
      entry.unitTo ??
      (effectiveSelectedQuantity
        ? (elementQuantityUnitRecord[
            effectiveSelectedQuantity
          ] as ConversionFactorUnit)
        : undefined),
  };
  const originMapping: ElementOriginMapping = {
    referenceId: recipe.id,
    referenceType: 'recipe',
    mappings: { [unitKey]: resolvedEntry },
  };
  const origin: ElementOrigin = {
    kind: 'csvImport',
    importId: row.importId,
    candidateRowId: row.id,
    mappingHash: hashImportMapping(originMapping),
    source: 'inline',
    mapping: originMapping,
  };

  let element = createElementWithCategory({
    kind: ElementKind.Element,
    category_id: recipe.category_id,
    name: candidateName(row) ?? row.name ?? '',
    origin,
  });

  // Write the converted Mängd to the chosen element quantity before applying the
  // recipe, so the recipe's child expressions inherit against it.
  const quantityName =
    effectiveSelectedQuantity ??
    getElementCategory(recipe.category_id)?.defaultSelectedQuantity;
  if (quantityName) {
    element = { ...element, selectedQuantity: quantityName };
    element = setElementQuantityProperties(element, [
      { name: quantityName as ElementQuantityExpressionName, count },
    ]);
  }

  // Best-effort sbef_code from CSV SBEF/kod/byggdel columns.
  const sbefCode = getSbefCode(candidateSbef(row));
  if (sbefCode) {
    element = setPropertyCountByNameOrId(
      element,
      ElementPropertyName.SbefCode,
      sbefCode,
    );
  }

  element = applyRecipe({ ...element, recipe_id: recipe.id }, recipe);

  return { ok: true, element };
};

/** Lookup context for {@link tryMaterializeCandidateRow}. */
export type TryMaterializeCandidateRowContext = {
  mappingsByCode: Record<string, ResourceCodeMapping | undefined>;
  productsById: Record<string, IProduct | undefined>;
  /**
   * Recipes keyed by id, for `referenceType: 'recipe'` mappings (DEV-3387).
   * Optional so non-recipe callers need not supply it; a recipe row without a
   * matching recipe here simply fails to materialize.
   */
  recipesById?: Record<string, Recipe | undefined>;
  importsById: Record<string, ProjectElementImport | undefined>;
  /**
   * Per-row inline mapping overlay from unsaved drafts (localStorage), keyed by
   * candidate-row id. When present for a row it fully replaces the DB mapping —
   * in-row editing is per-row, so it never leaks across rows sharing a code.
   */
  draftMappingsByRowId?: Record<string, ElementOriginMapping | undefined>;
  /**
   * Per-row typed total quantity (DEV-3369), keyed by candidate-row id. When
   * present for a row the materialized element's count carries it as a `value`
   * override on top of the conversion expression.
   */
  quantityOverridesByRowId?: Record<string, number | undefined>;
  /**
   * Quantity row to measure the element in (e.g. `area_top`), keyed by
   * candidate-row id. Drives the {@link materializeCandidate} `selectedQuantity`
   * override for in-row unit picking.
   */
  selectedQuantityByRowId?: Record<
    string,
    ElementQuantityExpressionName | undefined
  >;
  /**
   * Per-row IFC measure override, keyed by candidate-row id. When set,
   * quantity/unit cells are rewritten from that measure and auto-swap is
   * locked so the user's pick is not replaced (DEV-3577).
   */
  ifcMeasureByRowId?: Record<string, IfcMeasure | undefined>;
  /**
   * Stamped onto the resulting element's {@link ElementOrigin.trigger}. Import
   * callers pass `'auto'` for file-drop batch import, `'ai'` for AI mapping,
   * and `'user'` otherwise; defaults to `'user'` for ghost-preview /
   * baseline re-materialization.
   */
  trigger?: CsvImportTrigger;
};

export type TryMaterializeCandidateRowResult =
  { ok: true; element: IElement } | { ok: false };

/** The mapping that drives a row's materialization: per-row draft wins over the DB mapping. */
const resolveEffectiveMapping = (
  row: CandidateRow,
  ctx: TryMaterializeCandidateRowContext,
): { mapping: ElementOriginMapping; fromDraft: boolean } | undefined => {
  const draft = ctx.draftMappingsByRowId?.[row.id];
  if (draft?.referenceId) return { mapping: draft, fromDraft: true };

  if (!row.resourceCode) return undefined;
  const stored = ctx.mappingsByCode[row.resourceCode];
  if (!stored?.referenceId) return undefined;
  return {
    mapping: {
      referenceId: stored.referenceId,
      referenceType: stored.referenceType,
      mappings: stored.mappings,
    },
    fromDraft: false,
    // scope is read from the stored row below when flipping origin to 'stored'.
  };
};

/**
 * Resolve mapping entry + product for a row and run {@link materializeCandidate}.
 * Single entry point for import, ghost preview, and Edited baseline comparison.
 *
 * A per-row draft mapping (unsaved, inline) takes precedence over the DB mapping.
 * When the DB mapping wins, the element's origin is flipped from the default
 * `inline` snapshot to a `stored` reference (same hash) so the saved mapping is
 * the single source and drift can be detected later.
 */
export const tryMaterializeCandidateRow = (
  row: CandidateRow,
  ctx: TryMaterializeCandidateRowContext,
): TryMaterializeCandidateRowResult => {
  // A Quantity from file pick is relative to the mapping target -- "use this
  // column for this product". It is tried first but never locks the row: a
  // switch to a target that cannot express the picked measure would otherwise
  // strand the row on a manual factor the file already answers.
  const pinnedMeasure = resolveActiveIfcMeasure(
    row,
    ctx.ifcMeasureByRowId?.[row.id],
  );
  const sources = listImportQuantitySources(row, pinnedMeasure);
  const [firstSource] = sources;
  if (!firstSource) return { ok: false };

  const effective = resolveEffectiveMapping(row, ctx);
  if (!effective) return { ok: false };

  const { mapping, fromDraft } = effective;
  // An entry is keyed by the column it was calibrated against, which is the one
  // the import ran on -- not necessarily the row's own. Reading only the row's
  // own unit loses a rate saved against a swapped column.
  const entrySource =
    sources.find((s) => mapping.mappings[s.rowUnit] !== undefined) ??
    firstSource;
  const entry = mapping.mappings[entrySource.rowUnit] ?? {};
  // A saved rate owns the column it was calibrated against, so that column
  // leads. Without one the user's pick still steers the auto resolution.
  const measureForEntry =
    entry.quantityFactor === undefined ? pinnedMeasure : entrySource.measure;
  const trigger = ctx.trigger ?? 'user';
  const valueOverride = ctx.quantityOverridesByRowId?.[row.id];
  const selectedQuantity = ctx.selectedQuantityByRowId?.[row.id];

  try {
    let result: MaterializeResult;
    // Legacy / draft overlays may carry only a referenceId (product-only shape);
    // treat an absent referenceType as 'product' so they still materialize.
    const referenceType = mapping.referenceType ?? 'product';
    switch (referenceType) {
      case 'recipe': {
        const recipe = mapping.referenceId
          ? ctx.recipesById?.[mapping.referenceId]
          : undefined;
        if (!recipe) return { ok: false };
        result = materializeRecipeCandidate(
          row,
          entry,
          recipe,
          valueOverride,
          selectedQuantity,
          { pinnedMeasure: measureForEntry },
        );
        break;
      }
      case 'product': {
        const product = mapping.referenceId
          ? ctx.productsById[mapping.referenceId]
          : undefined;
        if (!product) return { ok: false };
        const wasteScale =
          ctx.importsById[row.importId]?.metadata?.interpretations?.wasteScale;
        result = materializeCandidate(
          row,
          entry,
          product,
          wasteScale,
          valueOverride,
          selectedQuantity,
          { pinnedMeasure: measureForEntry },
        );
        break;
      }
      default:
        return assertUnreachable(referenceType);
    }
    if (!result.ok) return { ok: false };

    if (fromDraft) {
      const element: IElement = result.element.origin
        ? {
            ...result.element,
            origin: { ...result.element.origin, trigger },
          }
        : result.element;
      return { ok: true, element };
    }

    // DB mapping won → reference it instead of carrying the inline snapshot.
    const stored = row.resourceCode
      ? ctx.mappingsByCode[row.resourceCode]
      : undefined;
    const scope = stored?.projectId != null ? 'project' : 'org';
    const element: IElement = {
      ...result.element,
      origin: {
        kind: 'csvImport',
        importId: row.importId,
        candidateRowId: row.id,
        mappingHash: result.element.origin?.mappingHash ?? '',
        trigger,
        source: 'stored',
        scope,
      },
    };
    return { ok: true, element };
  } catch {
    return { ok: false };
  }
};

/** Whether a row can materialize with the current org mapping (inverse of Incomplete). */
export const isCandidateRowMaterializable = (
  row: CandidateRow,
  ctx: TryMaterializeCandidateRowContext,
): boolean => tryMaterializeCandidateRow(row, ctx).ok;
